// Thin wrapper over the curation API. Everything is same-origin with the
// session cookie attached automatically, so there is no token handling here --
// that is the whole point of running the OIDC flow in the backend.

export type Status = "pending" | "approved" | "rejected" | "later";

export interface User {
  sub: string;
  name: string;
  email: string;
  roles: string[];
  admin: boolean;
}

export interface QueueItem {
  id: number;
  name: string;
  folder: string;
  // Non-empty when this build is one of several biome variants of the same
  // structure -- judging one can then cover the lot.
  group: string;
  status: Status;
  geometryEdited: boolean;
  claimedBy: string | null;
  decidedBy: string | null;
  // 0 lone, 1 settlement anchor, 2 settlement member -- shown in the list so a
  // settlement can be read off the queue instead of opened build by build.
  role: number;
  // Name of the settlement this build belongs to, for the same reason.
  settlement: string;
  // 0 = landmark, 1 = wayside prop.
  use: number;
}

export interface Structure {
  id: number;
  name: string;
  folder: string;
  source: string;
  status: Status;
  note: string;
  tierMin: number;
  tierMax: number;
  weight: number;
  yOffset: number;
  role: number;
  group: string;
  biomes: number[];
  prepareOnly: boolean;
  nameStyle: string;
  placement: number; // 0 surface, 1 underground, 2 sky, 3 liquid
  // What the build IS -- see USE in the inspector. Decides which placement
  // pass may use it, and is reported in the export for plugins.
  use: number;
  // Sink the build INTO the ground: only the air it encloses is carved, so it
  // does not stand in a rectangular trench.
  bury: boolean;
  yMin: number;
  yMax: number;
  geometryEdited: boolean;
  claimedBy: string | null;
  decidedBy: string | null;
  variantGroup: string;
  settlementId: number;
  // Extents in blocks. Zero until a client has parsed the build once.
  sizeX: number;
  sizeY: number;
  sizeZ: number;
}

// A settlement: an object in its own right, with members, rather than a string
// repeated on every build that belongs to it.
export interface Member {
  id: number;
  name: string;
  folder: string;
  role: number; // 1 anchor, 2 member
  size: number; // footprint in blocks, 0 until a client has measured it
  status: Status;
}

export interface Settlement {
  id: number;
  name: string;
  note: string;
  members: Member[];
  problems: string[];
}

export interface Progress {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  later: number;
}

export interface ProjectInfo {
  name: string;
  progress: Progress;
  folders: { folder: string; total: number; done: number }[];
  hasAssets: boolean;
  claimMinutes: number;
}

export class NotSignedIn extends Error {}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init.body && typeof init.body === "string"
        ? { "content-type": "application/json" }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) throw new NotSignedIn("not signed in");
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface NewStructure {
  name: string;
  folder: string;
  source: string;
  data: string; // base64 of the .schem bytes
  sx: number;
  sy: number;
  sz: number;
}

export const api = {
  me: () => req<{ user: User; project: string }>("/api/me"),
  project: () => req<ProjectInfo>("/api/project"),

  queue: (opts: {
    status?: Status | "";
    folder?: string;
    q?: string;
    placement?: string;
    use?: string;
    sort?: string;
  }) => {
    const p = new URLSearchParams();
    if (opts.status) p.set("status", opts.status);
    if (opts.folder) p.set("folder", opts.folder);
    if (opts.q) p.set("q", opts.q);
    if (opts.placement) p.set("placement", opts.placement);
    if (opts.use) p.set("use", opts.use);
    if (opts.sort) p.set("sort", opts.sort);
    return req<{ items: QueueItem[]; progress: Progress }>(
      `/api/queue?${p.toString()}`,
    );
  },

  // Claim one specific structure -- what opening it from the list does.
  // `ok:false` means somebody else is holding it, and `heldBy` says who.
  claimOne: (id: number) =>
    req<{ ok: boolean; heldBy?: string }>("/api/claim-one", {
      method: "POST",
      body: JSON.stringify({ id }),
    }),

  // Ask the server for the next structure nobody else is working on. Returns
  // null when the queue is empty -- the shared-queue equivalent of "done".
  claim: (folder?: string) =>
    req<{ id: number | null }>("/api/claim", {
      method: "POST",
      body: JSON.stringify({ folder }),
    }),

  structure: (id: number) => req<Structure>(`/api/structures/${id}`),

  patch: (id: number, fields: Partial<Record<string, unknown>>) =>
    req<Structure>(`/api/structures/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  verdict: (id: number, status: Status) =>
    req<{ ok: boolean; progress: Progress }>(`/api/structures/${id}/verdict`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  release: (id: number) =>
    req<{ ok: boolean }>(`/api/structures/${id}/release`, { method: "POST" }),

  // The raw .schem, handed straight to the WebAssembly core.
  schem: async (id: number): Promise<Uint8Array> => {
    const res = await fetch(`/api/structures/${id}/schem`, {
      credentials: "same-origin",
    });
    if (res.status === 401) throw new NotSignedIn("not signed in");
    if (!res.ok) throw new Error(`could not load structure ${id}`);
    return new Uint8Array(await res.arrayBuffer());
  },

  // Replace the geometry after a rotate/mirror/trim done in the browser.
  putSchem: async (id: number, bytes: Uint8Array): Promise<void> => {
    const res = await fetch(`/api/structures/${id}/schem`, {
      method: "PUT",
      credentials: "same-origin",
      body: bytes as BodyInit,
    });
    if (!res.ok) throw new Error("could not save the edited structure");
  },

  // Builds added to the open project, without touching what is already in it.
  // The bytes are ".schem" -- a structure-block ".nbt" was turned into one by
  // the WebAssembly core before it got here, so the server stores one format.
  addStructures: (items: NewStructure[]) =>
    req<{ added: number; skipped: number }>("/api/structures/add", {
      method: "POST",
      body: JSON.stringify({ items }),
    }),

  importProject: async (json: string, name: string, replace: boolean) => {
    const res = await fetch(
      `/api/import?name=${encodeURIComponent(name)}&replace=${replace ? 1 : 0}`,
      { method: "POST", credentials: "same-origin", body: json },
    );
    const body = (await res.json()) as { error?: string; imported?: number };
    if (!res.ok) throw new Error(body.error ?? "import failed");
    return body as { imported: number; skipped: number };
  },

  // Copy this structure's settings (and optionally its verdict) onto its
  // variant siblings or the rest of its folder.
  batch: (
    id: number,
    scope: "group" | "folder",
    opts: {
      fields?: boolean;
      status?: Status;
      onlyPending?: boolean;
    } = {},
  ) =>
    req<{ changed: number; progress: Progress }>("/api/batch", {
      method: "POST",
      body: JSON.stringify({ id, scope, ...opts }),
    }),

  settlements: () =>
    req<{
      settlements: Settlement[];
      maxMemberFootprint: number;
      unmeasured: number[];
    }>("/api/settlements"),

  createSettlement: (name: string) =>
    req<{ settlements: Settlement[] }>("/api/settlements", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  editSettlement: (id: number, fields: { name?: string; note?: string }) =>
    req<{ settlements: Settlement[] }>(`/api/settlements/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  deleteSettlement: (id: number) =>
    req<{ settlements: Settlement[] }>(`/api/settlements/${id}`, {
      method: "DELETE",
    }),

  // Adding a build and changing whether it is the anchor are the same write:
  // a member IS its role within a settlement.
  addMembers: (id: number, ids: number[], role: number) =>
    req<{ moved: number; settlements: Settlement[] }>(
      `/api/settlements/${id}/members`,
      { method: "POST", body: JSON.stringify({ ids, role }) },
    ),

  removeMembers: (id: number, ids: number[]) =>
    req<{ settlements: Settlement[] }>(`/api/settlements/${id}/members`, {
      method: "DELETE",
      body: JSON.stringify({ ids }),
    }),

  // Hand the extents back to the server, which has no .schem parser of its own.
  reportDims: (id: number, sx: number, sy: number, sz: number) =>
    req<{ ok: boolean }>(`/api/structures/${id}/dims`, {
      method: "POST",
      body: JSON.stringify({ sx, sy, sz }),
    }),

  biomes: () =>
    req<{ id: number; name: string; label: string; mc: string }[]>(
      "/api/biomes",
    ),
};

// Live updates. The browser reconnects on its own, so this just wires the
// callback up and hands back a stop function.
export type LiveEvent =
  | { type: "claim" | "release" | "geometry"; id: number; by: string }
  | { type: "verdict"; id: number; by: string; status: Status }
  | { type: "edit"; id: number; by: string; fields: string[] }
  | { type: "settlement"; by: string }
  | { type: "import"; by: string; count: number }
  | { type: "presence"; users: { sub: string; name: string }[] };

export function subscribeLive(onEvent: (e: LiveEvent) => void): () => void {
  const es = new EventSource("/api/live", { withCredentials: true });
  es.onmessage = (m) => {
    try {
      onEvent(JSON.parse(m.data) as LiveEvent);
    } catch {
      /* a comment line or a partial frame: ignore */
    }
  };
  return () => es.close();
}

export function loginHref(): string {
  return `/auth/login?to=${encodeURIComponent(
    location.pathname + location.search,
  )}`;
}
